// Image and Video generation modals.
import React, { useState, useEffect } from 'react';
import { X, Sparkles, Download, Video, Image as ImageIcon, Wand2, Cpu } from 'lucide-react';
import { generateImage, generateVideo, pollVideoUntilDone, generateStabilityImage, generateSora2Video, fetchCapabilities, generatePollinationsImage } from '../lib/media';
import { useToast } from './Toast';

const downloadBase64Image = (b64, name = 'ynx-image.png') => {
  const link = document.createElement('a');
  link.href = `data:image/png;base64,${b64}`;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const ImageGenModal = ({ open, onClose, referenceImageBase64 = null, onAttach }) => {
  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // base64
  const [text, setText] = useState('');
  const [engine, setEngine] = useState('pollinations'); // pollinations | nano-banana | stab-ultra | stab-sd35 | stab-core
  const [aspect, setAspect] = useState('1:1');
  const [creditsLeft, setCreditsLeft] = useState(null);

  useEffect(() => {
    if (open) {
      fetchCapabilities().then((caps) => {
        if (typeof caps.stability_credits === 'number') setCreditsLeft(caps.stability_credits);
      });
    }
  }, [open]);

  if (!open) return null;

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast('Décris l\'image que tu veux', 'error'); return; }
    setBusy(true);
    setResult(null);
    try {
      if (engine === 'pollinations') {
        const aspectMap = { '1:1': [1024, 1024], '16:9': [1536, 864], '9:16': [864, 1536], '21:9': [1792, 768], '4:5': [1024, 1280], '3:2': [1280, 853] };
        const [w, h] = aspectMap[aspect] || [1024, 1024];
        const data = await generatePollinationsImage({ prompt: prompt.trim(), model: 'flux', width: w, height: h });
        if (data.image_b64) {
          setResult(data.image_b64);
          setText(`Pollinations Flux · seed ${data.seed} · gratuit`);
          toast('Image générée (Pollinations Flux · gratuit)', 'success');
        } else {
          toast('Aucune image — réessaie', 'error');
        }
      } else if (engine === 'nano-banana') {
        const data = await generateImage({
          prompt: prompt.trim(),
          referenceImageBase64,
        });
        const first = (data.images || [])[0];
        if (first?.data_base64) {
          setResult(first.data_base64);
          setText(data.text || '');
          toast('Image générée (Nano Banana)', 'success');
        } else {
          toast('Aucune image — essaie un prompt plus précis', 'error');
          setText(data.text || '');
        }
      } else {
        const stabModel = engine === 'stab-ultra' ? 'ultra' : engine === 'stab-sd35' ? 'sd3.5-large' : 'core';
        const data = await generateStabilityImage({
          prompt: prompt.trim(),
          model: stabModel,
          aspectRatio: aspect,
        });
        if (data.image_b64) {
          setResult(data.image_b64);
          setText(`Stability ${stabModel} · seed ${data.seed} · ${data.finish_reason}`);
          setCreditsLeft(data.credits_left);
          toast(`Image générée (Stability ${stabModel})`, 'success');
        } else {
          toast('Aucune image retournée', 'error');
        }
      }
    } catch (err) {
      console.warn('YNX: image gen failed', err);
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setPrompt('');
    setResult(null);
    setText('');
    onClose();
  };

  return (
    <div className='modal-overlay active' onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className='modal' style={{ maxWidth: 680 }}>
        <div className='modal-header'>
          <span className='modal-title'>
            {referenceImageBase64 ? 'TRANSFORMER L\'IMAGE' : 'GÉNÉRER UNE IMAGE'}
          </span>
          <button className='modal-close' onClick={handleClose}><X size={14} /></button>
        </div>
        <div className='modal-body'>
          {referenceImageBase64 && (
            <div className='modal-section'>
              <label className='modal-label'>▸ Image de référence</label>
              <img
                src={`data:image/png;base64,${referenceImageBase64}`}
                alt='reference'
                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, border: 'var(--hud-border)' }}
              />
            </div>
          )}

          {!referenceImageBase64 && (
            <div className='modal-section'>
              <label className='modal-label'><Cpu size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} /> MOTEUR D&apos;IMAGE</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                {[
                  { id: 'pollinations', name: 'Pollinations Flux ✨', sub: '100% gratuit · sans crédit · sans limite' },
                  { id: 'nano-banana', name: 'Nano Banana 🍌', sub: 'Gemini · gratuit (Universal Key)' },
                  { id: 'stab-ultra', name: 'Stability Ultra', sub: `~8 crédits · best quality${creditsLeft != null ? ` · ${creditsLeft} restants` : ''}` },
                  { id: 'stab-sd35', name: 'SD 3.5 Large', sub: '~3 crédits · équilibré' },
                  { id: 'stab-core', name: 'Stability Core', sub: '1 crédit · rapide' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type='button'
                    onClick={() => setEngine(opt.id)}
                    disabled={busy}
                    data-testid={`imggen-engine-${opt.id}`}
                    style={{
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: 4,
                      border: engine === opt.id ? '1px solid var(--cyan)' : '1px solid var(--wire)',
                      background: engine === opt.id ? 'rgba(0,229,255,0.08)' : 'transparent',
                      color: engine === opt.id ? 'var(--cyan)' : 'var(--text-bright)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{opt.name}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-dim)' }}>{opt.sub}</div>
                  </button>
                ))}
              </div>
              {(engine === 'pollinations' || engine !== 'nano-banana') && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['1:1', '16:9', '9:16', '21:9', '4:5', '3:2'].map((r) => (
                    <button key={r} type='button' onClick={() => setAspect(r)} disabled={busy}
                      style={{
                        padding: '3px 9px',
                        borderRadius: 12,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        background: aspect === r ? 'var(--cyan)' : 'transparent',
                        color: aspect === r ? '#000' : 'var(--text-dim)',
                        border: '1px solid var(--wire)',
                        cursor: 'pointer',
                      }}>
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className='modal-section'>
            <label className='modal-label'>
              ▸ {referenceImageBase64 ? 'Comment transformer cette image ?' : 'Décris l\'image souhaitée'}
            </label>
            <textarea
              data-testid='imggen-prompt'
              name='imggen-prompt'
              className='hud-textarea'
              rows={3}
              placeholder={referenceImageBase64
                ? 'Ex: rendre cette photo en style cyberpunk, ajouter des néons bleus, fond futuriste'
                : 'Ex: portrait professionnel haute qualité d\'un homme en costume, éclairage de studio, fond bleu'}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={busy}
            />
          </div>

          {result && (
            <div className='modal-section'>
              <label className='modal-label'>▸ Résultat</label>
              <img
                src={`data:image/png;base64,${result}`}
                alt='generated'
                style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid var(--cyan)' }}
              />
              {text && <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 8 }}>{text}</p>}
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button className='hud-btn primary' onClick={() => downloadBase64Image(result, `ynx-image-${Date.now()}.png`)}>
                  <Download size={12} /> TÉLÉCHARGER
                </button>
                {onAttach && (
                  <button className='hud-btn' onClick={() => { onAttach(result); handleClose(); }}>
                    <ImageIcon size={12} /> JOINDRE AU CHAT
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className='modal-footer'>
          <button className='hud-btn' onClick={handleClose} disabled={busy}>FERMER</button>
          <button data-testid='imggen-btn' className='hud-btn primary' onClick={handleGenerate} disabled={busy || !prompt.trim()}>
            {busy ? <><span className='spin-ring' /> GÉNÉRATION…</> : <><Sparkles size={12} /> GÉNÉRER</>}
          </button>
        </div>
      </div>
    </div>
  );
};


export const VideoGenModal = ({ open, onClose, sourceImageBase64 = null, onSelectImage }) => {
  const toast = useToast();
  const [imageB64, setImageB64] = useState(sourceImageBase64);
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(5);
  const [ratio, setRatio] = useState('1280:720');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [engine, setEngine] = useState('runway'); // runway | sora-2 | sora-2-pro
  const [soraSize, setSoraSize] = useState('1280x720');
  const [soraSeconds, setSoraSeconds] = useState('4');

  React.useEffect(() => {
    if (open) setImageB64(sourceImageBase64);
  }, [open, sourceImageBase64]);

  if (!open) return null;

  const handlePickFile = async (file) => {
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => {
      const result = fr.result;
      if (typeof result === 'string') {
        const idx = result.indexOf(',');
        setImageB64(idx >= 0 ? result.slice(idx + 1) : result);
      }
    };
    fr.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (engine === 'runway' && !imageB64) {
      toast('Runway nécessite une image source. Bascule sur Sora 2 pour text-to-video.', 'error');
      return;
    }
    if (engine !== 'runway' && !prompt.trim()) {
      toast('Décris la vidéo que tu veux (Sora 2 = text-to-video)', 'error');
      return;
    }
    setBusy(true);
    setProgress({ status: 'PENDING', progress: 0 });
    setVideoUrl(null);
    try {
      if (engine === 'runway') {
        const { task_id } = await generateVideo({
          promptImageBase64: imageB64,
          promptText: prompt.trim(),
          duration,
          ratio,
        });
        if (!task_id) throw new Error('Pas de task_id retourné par Runway');
        const data = await pollVideoUntilDone(task_id, {
          onProgress: ({ status, progress }) => setProgress({ status, progress: progress || 0 }),
        });
        const url = (data.output && data.output[0]) || null;
        if (!url) throw new Error('Aucune URL vidéo retournée');
        setVideoUrl(url);
        toast('Vidéo prête (Runway) !', 'success');
      } else {
        setProgress({ status: 'GÉNÉRATION SORA 2…', progress: 0 });
        const data = await generateSora2Video({
          prompt: prompt.trim(),
          model: engine,
          size: soraSize,
          seconds: soraSeconds,
        });
        // Build a data URL for the mp4 from base64
        const url = `data:video/mp4;base64,${data.video_b64}`;
        setVideoUrl(url);
        toast(`Vidéo prête (${engine}) !`, 'success');
      }
    } catch (err) {
      console.warn('YNX: video gen failed', err);
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setPrompt('');
    setVideoUrl(null);
    setProgress(null);
    setImageB64(null);
    onClose();
  };

  return (
    <div className='modal-overlay active' onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className='modal' style={{ maxWidth: 680 }}>
        <div className='modal-header'>
          <span className='modal-title'>GÉNÉRER UNE VIDÉO</span>
          <button className='modal-close' onClick={handleClose}><X size={14} /></button>
        </div>
        <div className='modal-body'>
          <div className='modal-section'>
            <label className='modal-label'><Cpu size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} /> MOTEUR VIDÉO</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {[
                { id: 'runway', name: 'Runway gen3', sub: 'image → vidéo · 5-10s' },
                { id: 'sora-2', name: 'Sora 2', sub: 'text → vidéo · 4/8/12s' },
                { id: 'sora-2-pro', name: 'Sora 2 Pro', sub: 'qualité max · plus long' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type='button'
                  onClick={() => setEngine(opt.id)}
                  disabled={busy}
                  data-testid={`videogen-engine-${opt.id}`}
                  style={{
                    textAlign: 'left',
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: engine === opt.id ? '1px solid var(--cyan)' : '1px solid var(--wire)',
                    background: engine === opt.id ? 'rgba(0,229,255,0.08)' : 'transparent',
                    color: engine === opt.id ? 'var(--cyan)' : 'var(--text-bright)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>{opt.name}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-dim)' }}>{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>

          <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14, lineHeight: 1.5 }}>
            {engine === 'runway' ? 'Runway gen3a_turbo : anime une image source en vidéo réaliste (5 ou 10 secondes). Coût ≈ 0,05 $/s.'
              : `OpenAI ${engine} : génère une vidéo directement à partir d'un prompt texte. Sora utilise la facturation de ta clé OpenAI directe.`}
          </p>

          {engine !== 'runway' && (
            <div className='modal-section' style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label className='modal-label'>FORMAT</label>
                <select name='sora-size' className='hud-select' value={soraSize} onChange={(e) => setSoraSize(e.target.value)} disabled={busy}>
                  <option value='1280x720'>1280×720 (HD paysage)</option>
                  <option value='1792x1024'>1792×1024 (cinéma)</option>
                  <option value='1024x1792'>1024×1792 (portrait)</option>
                  <option value='1024x1024'>1024×1024 (carré)</option>
                </select>
              </div>
              <div>
                <label className='modal-label'>DURÉE</label>
                <select name='sora-seconds' className='hud-select' value={soraSeconds} onChange={(e) => setSoraSeconds(e.target.value)} disabled={busy}>
                  <option value='4'>4 secondes</option>
                  <option value='8'>8 secondes</option>
                  <option value='12'>12 secondes</option>
                </select>
              </div>
            </div>
          )}

          <div className='modal-section'>
            <label className='modal-label'>▸ Image source {engine !== 'runway' && '(optionnel pour Sora)'}</label>
            {imageB64 ? (
              <img
                src={`data:image/png;base64,${imageB64}`}
                alt='source'
                style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 6, border: '1px solid var(--cyan)' }}
              />
            ) : (
              <input
                type='file'
                name='video-source-file'
                accept='image/png,image/jpeg,image/webp'
                className='hud-input'
                onChange={(e) => handlePickFile(e.target.files[0])}
              />
            )}
          </div>

          <div className='modal-section'>
            <label className='modal-label'>▸ {engine === 'runway' ? 'Direction de l\'animation (optionnel)' : 'Décris la scène vidéo'}</label>
            <textarea
              data-testid='videogen-prompt'
              name='videogen-prompt'
              className='hud-textarea'
              rows={engine === 'runway' ? 2 : 4}
              placeholder={engine === 'runway'
                ? 'Ex: la personne sourit doucement, caméra travelling lent vers la droite'
                : 'Ex: Un chat astronaute flottant dans une nébuleuse violette, plan moyen, éclairage cinématique, mouvement de caméra latéral lent'}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={busy}
            />
          </div>

          {engine === 'runway' && (
            <>
              <div className='modal-section'>
                <label className='modal-label'>▸ Format</label>
                <select name='runway-ratio' className='hud-select' value={ratio} onChange={(e) => setRatio(e.target.value)} disabled={busy} style={{ maxWidth: 'none' }}>
                  <option value='1280:720'>Paysage 16:9 (1280×720)</option>
                  <option value='720:1280'>Portrait 9:16 (720×1280) — réseaux sociaux</option>
                  <option value='960:960'>Carré 1:1 (960×960) — Instagram</option>
                  <option value='1104:832'>Paysage 4:3 (1104×832)</option>
                  <option value='832:1104'>Portrait 3:4 (832×1104)</option>
                  <option value='1584:672'>Ultra-wide (1584×672) — cinéma</option>
                </select>
              </div>
            </>
          )}

          {engine === 'runway' && (
            <div className='modal-section'>
              <label className='modal-label'>▸ Durée</label>
              <select name='runway-duration' className='hud-select' value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))} disabled={busy}>
                <option value={5}>5 secondes (~0,25 $)</option>
                <option value={10}>10 secondes (~0,50 $)</option>
              </select>
            </div>
          )}

          {progress && (
            <div className='modal-section' style={{ background: 'var(--hull)', borderRadius: 6, padding: 12 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--cyan)' }}>
                ▸ {progress.status} {progress.progress ? `· ${Math.round(progress.progress * 100)}%` : ''}
              </div>
              <div style={{ marginTop: 6, height: 4, background: 'var(--void)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${(progress.progress || 0) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--cyan), var(--neon))', transition: 'width 0.3s' }} />
              </div>
            </div>
          )}

          {videoUrl && (
            <div className='modal-section'>
              <label className='modal-label'>▸ Vidéo générée</label>
              <video src={videoUrl} controls style={{ width: '100%', borderRadius: 6, border: '1px solid var(--neon)' }} />
              <div style={{ marginTop: 8 }}>
                <a href={videoUrl} target='_blank' rel='noreferrer' className='hud-btn primary' style={{ textDecoration: 'none' }}>
                  <Download size={12} /> OUVRIR DANS UN ONGLET
                </a>
              </div>
            </div>
          )}
        </div>
        <div className='modal-footer'>
          <button className='hud-btn' onClick={handleClose} disabled={busy}>FERMER</button>
          <button
            data-testid='videogen-btn'
            className='hud-btn primary'
            onClick={handleGenerate}
            disabled={busy || (engine === 'runway' ? !imageB64 : !prompt.trim())}
          >
            {busy ? <><span className='spin-ring' /> EN COURS…</> : <><Video size={12} /> {engine === 'runway' ? 'ANIMER' : 'GÉNÉRER'}</>}
          </button>
        </div>
      </div>
    </div>
  );
};
